import { create } from "zustand";
import {
  getHubOrigin,
  HUB_PREVIEW_PATH,
  isHubToPreviewMessage,
  type HubPreviewVisibility,
  type PreviewToHubMessage,
} from "./hubPreviewProtocol";

const INIT_TIMEOUT_MS = 15_000;
const TOKEN_TIMEOUT_MS = 10_000;
/** Preview tokens last about 10 minutes. The refresh clock starts when the token arrives, not from the server's absolute expiry. */
const TOKEN_LIFETIME_MS = 10 * 60_000;
/** Ask the Hub for a new token this long before the local deadline. */
const TOKEN_REFRESH_BEFORE_EXPIRY_MS = 60_000;

const hubOrigin = typeof window === "undefined" ? null : getHubOrigin(window.location.hostname);

/**
 * True when this page was opened by the Hub's merchant dashboard preview: the Hub loads `/hub-preview` in an iframe.
 * Decided once at load, before the router redirects away from `/hub-preview`.
 */
export const IS_HUB_PREVIEW =
  hubOrigin !== null &&
  typeof window !== "undefined" &&
  window.parent !== window &&
  window.location.pathname === HUB_PREVIEW_PATH;

interface HubPreviewState {
  readonly merchantId: string | null;
  /** The Hub's draft visibility, which replaces the merchant's saved settings while previewing. */
  readonly visibility: HubPreviewVisibility | null;
}

export const useHubPreviewStore = create<HubPreviewState>(() => ({ merchantId: null, visibility: null }));

let previewToken: string | null = null;
let previewTokenExpiresAt = 0;
let pendingToken: Promise<string | null> | null = null;
let resolveToken: ((token: string | null) => void) | null = null;
let navigateHandler: ((path: string) => void) | null = null;
let connection: Promise<string> | null = null;

const toVisibility = ({ hiddenPages, hiddenMetrics, hiddenReports }: HubPreviewVisibility): HubPreviewVisibility => ({
  hiddenPages,
  hiddenMetrics,
  hiddenReports,
});

function postToHub(message: PreviewToHubMessage) {
  if (hubOrigin) window.parent.postMessage(message, hubOrigin);
}

function setToken(token: string, expiresAt: string) {
  previewToken = token;
  const receivedAt = Date.now();
  const serverExpiry = Date.parse(expiresAt);
  const remaining = Number.isNaN(serverExpiry) ? TOKEN_LIFETIME_MS : serverExpiry - receivedAt;
  // A clock that is ahead makes `remaining` tiny or negative, which would ask for a new token on every call.
  // An unparseable expiry does the same. Both fall back to the token's normal lifetime, counted locally.
  const usable = remaining > TOKEN_REFRESH_BEFORE_EXPIRY_MS && remaining <= TOKEN_LIFETIME_MS
    ? remaining
    : TOKEN_LIFETIME_MS;
  previewTokenExpiresAt = receivedAt + usable;
  resolveToken?.(token);
  resolveToken = null;
  pendingToken = null;
}

/**
 * Connects to the Hub: announces the app is ready and waits for the merchant id, a preview token and the draft
 * visibility. Rejects if the Hub does not answer. Connects once, however often it is called.
 */
export function connectHubPreview(): Promise<string> {
  connection ??= new Promise((resolve, reject) => {
    const timeout = setTimeout(() => reject(new Error("The Hub did not start the preview")), INIT_TIMEOUT_MS);

    window.addEventListener("message", (event: MessageEvent) => {
      if (event.origin !== hubOrigin || event.source !== window.parent || !isHubToPreviewMessage(event.data)) return;
      const message = event.data;

      switch (message.type) {
        case "mezza-preview:init":
          clearTimeout(timeout);
          setToken(message.previewToken, message.expiresAt);
          useHubPreviewStore.setState({
            merchantId: message.merchantId,
            visibility: toVisibility(message),
          });
          resolve(message.merchantId);
          break;
        case "mezza-preview:visibility": {
          const current = useHubPreviewStore.getState().visibility;
          const sameList = (left: readonly string[] | undefined, right: readonly string[]) =>
            left?.length === right.length && left.every((item, index) => item === right[index]);
          if (
            current &&
            sameList(current.hiddenPages, message.hiddenPages) &&
            sameList(current.hiddenMetrics, message.hiddenMetrics) &&
            sameList(current.hiddenReports, message.hiddenReports)
          ) {
            break;
          }
          useHubPreviewStore.setState({ visibility: toVisibility(message) });
          break;
        }
        case "mezza-preview:token":
          setToken(message.previewToken, message.expiresAt);
          break;
        case "mezza-preview:navigate":
          navigateHandler?.(message.path);
          break;
      }
    });

    postToHub({ type: "mezza-preview:ready" });
  });
  return connection;
}

/** The preview token, asking the Hub for a new one when forced (after a 401) or close to expiry. */
export function getHubPreviewToken(forceRefresh: boolean): Promise<string | null> {
  if (previewToken && !forceRefresh && Date.now() < previewTokenExpiresAt - TOKEN_REFRESH_BEFORE_EXPIRY_MS) {
    return Promise.resolve(previewToken);
  }

  if (!pendingToken) {
    pendingToken = new Promise((resolve) => {
      resolveToken = resolve;
      setTimeout(() => {
        if (resolveToken === resolve) {
          resolveToken = null;
          pendingToken = null;
          resolve(previewToken);
        }
      }, TOKEN_TIMEOUT_MS);
    });
    postToHub({ type: "mezza-preview:token-request" });
  }

  return pendingToken;
}

/** Lets the Hub move the preview between dashboard pages, and tells the Hub where it is. */
export function setHubPreviewNavigator(handler: ((path: string) => void) | null) {
  navigateHandler = handler;
}

export function reportHubPreviewLocation(path: string) {
  postToHub({ type: "mezza-preview:navigated", path });
}

/** The dashboard's first content has painted, so the Hub can drop its loading overlay. */
export function reportHubPreviewPainted() {
  postToHub({ type: "mezza-preview:painted" });
}
